import { anchoredPos, getZeroAnchoredEntity, setParent } from '../entity';
import { t } from '../localizationController';
import { addV2, cloneTextStyle, createAnchoredPosFromSide, v2 } from '../helpers';
import {
  Alignment_Horizontal,
  EventClientToServer,
  Side,
  type JSON_SetUserFlag,
} from '../shared/api_shared_types';
import { setVisibilityState } from '../simulate';
import {
  MoveMode,
  TextAppearStyle,
  VisibilityState,
  type AnchoredPosition,
  type ButtonStyle,
  type GameManager,
  type GameObject,
  type IconButton,
  type RenderShapeStyle,
  type Size,
  type TextButton,
  type TextObject,
  type TextObjectStyle,
  type ToolTip,
  type V2,
} from '../types';
import { COLORS, SHAPE_STYLES, TEXT_STYLE } from '../constants';
import { changeTextObjectText, createAndAddLocalTextObject } from '../textobject/textobject';
import { createAndAddLocalToolTip } from '../tooltip';
import { createAndAddLocalIconButton } from '../icon_button';
import { createAndAddLocalTextButton } from '../textbutton';
import logger from './logger';

const toolTipMap: Map<GameObject, ToolTip> = new Map();

const SIMPLE: TextObjectStyle = {
  ...TEXT_STYLE.CONTENT_DEFAULT,
  color: COLORS.GUNMETAL,
  colorHighlight: COLORS.GUNMETAL,
  defaultTextColor: COLORS.GAINSBORO,
  fontSizeEm: 0.7,
  padding: [8, 5, 8, 5],
  roundedSides: [true, true],
  doAutoResize: false,
  textAlign: Alignment_Horizontal.CENTER,
};

const TIP: TextObjectStyle = {
  ...TEXT_STYLE.CONTENT_DEFAULT,
  color: COLORS.WHITE_100,
  colorHighlight: COLORS.WHITE_100,
  defaultTextColor: COLORS.BLACK_100,
  fontSizeEm: 0.8,
  padding: [16, 16, 16, 16],
  cornerRadius: [20, 20, 20, 20],
  roundedSides: [false, false],
  doAutoResize: false,
  textAlign: Alignment_Horizontal.LEFT,
};

const NEXT: TextObjectStyle = {
  ...TEXT_STYLE.CONTENT_DEFAULT,
  color: COLORS.WHITE_0,
  colorHighlight: [...COLORS.NEW_STYLE.PURPLE_LIGHT, 50],
  defaultTextColor: COLORS.BLACK_75,
  fontSizeEm: 0.8,
  padding: [10, 5, 10, 5],
  cornerRadius: [5, 5, 5, 5],
  roundedSides: [false, false],
  doAutoResize: false,
  textAlign: Alignment_Horizontal.LEFT,
};

const TIP_STICKY: TextObjectStyle = {
  ...TEXT_STYLE.CONTENT_DEFAULT,
  defaultTextColor: COLORS.BLACK_100,
  fontSizeEm: 0.8,
  padding: [28, 46, 28, 28],
  cornerRadius: [0, 0, 0, 0],
  roundedSides: [false, false],
  doAutoResize: false,
  textAlign: Alignment_Horizontal.LEFT,
};

const CLOSE: ButtonStyle = {
  icon: 'iconQuitBlack',
  color: COLORS.TRANSPARENT,
  colorHighlight: COLORS.WHITE_25,
  size: 14,
  iconSize: 20,
  hoverShadow: false,
};

const BUTTON_PADDING = 40;

function createToolTipBase(
  manager: GameManager,
  parentObj: GameObject,
  anchoredPosition: AnchoredPosition,
  shapeStyle: RenderShapeStyle,
  arrowSide: Side,
): ToolTip {
  const toolTip = createAndAddLocalToolTip(manager, getZeroAnchoredEntity());
  toolTip.shapeStyle = { ...shapeStyle };
  toolTip.anchoredPos = anchoredPosition;
  toolTip.arrowSide = arrowSide;
  setParent(toolTip, parentObj);
  setVisibilityState(toolTip, VisibilityState.DEFAULT);
  return toolTip;
}

export function isToolTipActiveForObject(gameObject: GameObject): boolean {
  let toolTip: ToolTip = toolTipMap.get(gameObject);
  if (toolTip) {
    return true;
  } else {
    return false;
  }
}

function createToolTipTextObj(
  manager: GameManager,
  toolTip: ToolTip,
  text: string,
  textStyle: TextObjectStyle,
  relativeWidth: number,
): TextObject {
  const tObj = createAndAddLocalTextObject(
    manager,
    getZeroAnchoredEntity(),
    text,
    cloneTextStyle(textStyle),
    relativeWidth,
    false,
    0,
    1,
    TextAppearStyle.FADE,
  );
  tObj.appearDuration = 0.1;
  tObj.onlyShowInDefaultState = false;
  setParent(tObj, toolTip);
  return tObj;
}

function createNavButton(toolTip: ToolTip, label: string, pos: AnchoredPosition): TextButton {
  const btn = createAndAddLocalTextButton(
    toolTip.manager,
    getZeroAnchoredEntity(),
    label,
    '',
    NEXT,
    0.4,
    0.4,
    0,
    0.1,
  );
  btn.anchoredPos = pos;
  setParent(btn, toolTip);
  setVisibilityState(btn, VisibilityState.DEFAULT);
  return btn;
}

function nextPage(toolTip: ToolTip) {
  if (toolTip.currentPage >= toolTip.text.length - 1) {
    return;
  }

  toolTip.currentPage += 1;

  if (toolTip.tObj) {
    changeTextObjectText(toolTip.tObj, toolTip.text[toolTip.currentPage]);
    toolTip.height = toolTip.tObj.height + BUTTON_PADDING;
  }

  if (!toolTip.btnPrev) {
    toolTip.btnPrev = createNavButton(toolTip, t('overlay.btn_back'), anchoredPos(v2(0, 1), v2(20, -5), v2(0, 1)));
  }

  if (
    toolTip.btnNext &&
    toolTip.currentPage >= toolTip.text.length - 1 &&
    !toolTip.showNextButton
  ) {
    setVisibilityState(toolTip.btnNext, VisibilityState.DISAPPEARING);
    toolTip.btnNext = null;
  }
}

function prevPage(toolTip: ToolTip) {
  if (toolTip.currentPage <= 0) {
    return;
  }

  toolTip.currentPage -= 1;

  if (toolTip.tObj) {
    changeTextObjectText(toolTip.tObj, toolTip.text[toolTip.currentPage]);
    toolTip.height = toolTip.tObj.height + BUTTON_PADDING;
  }

  if (toolTip.currentPage <= 0 && toolTip.btnPrev) {
    setVisibilityState(toolTip.btnPrev, VisibilityState.DISAPPEARING);
    toolTip.btnPrev = null;
  }

  if (!toolTip.btnNext && toolTip.currentPage < toolTip.text.length - 1) {
    toolTip.btnNext = createNavButton(
      toolTip,
      t('overlay.btn_next'),
      anchoredPos(v2(1, 1), v2(-20, -5), v2(1, 1)),
    );
  }
}

export function updateToolTips() {
  const now = Date.now();
  for (const [obj, toolTip] of toolTipMap) {
    if (toolTip.shownAt > 0 && now - toolTip.shownAt >= TIMED_DURATION_MS) {
      hideToolTip(obj);
    } else if (toolTip.btnClose?.clickedThisFrame) {
      hideToolTip(obj, true, true, true);
    } else if (toolTip.btnNext?.clickedThisFrame) {
      if (toolTip.currentPage === toolTip.text.length - 1 && toolTip.showNextButton) {
        toolTip.disappearDuration = 0;
        hideToolTip(obj, true, true, true);
      } else {
        nextPage(toolTip);
      }
    } else if (toolTip.btnPrev?.clickedThisFrame) {
      prevPage(toolTip);
    }
  }
}

export function showStickyToolTip(
  gameObject: GameObject,
  text: string[],
  flag: string,
  anchoredPosition: AnchoredPosition = null,
  arrowSide: Side,
  arrowOffset = 0,
  size: Size = { width: 250, height: 250 },
  onFlagSet: ((doContinue: boolean) => void) | null = null,
  showNextButton = false,
) {
  if (!gameObject || !text || !text.length) return;

  let toolTip: ToolTip = toolTipMap.get(gameObject);

  const manager = gameObject.manager;

  if (!anchoredPosition) {
    anchoredPosition = createAnchoredPosFromSide(Side.ABOVE);
  }

  if (!toolTip) {
    toolTip = createToolTipBase(
      manager,
      gameObject,
      anchoredPosition,
      SHAPE_STYLES.WHITE,
      arrowSide,
    );
    toolTip.width = size.width;
    toolTip.height = size.height;

    toolTip.cornerRadius = 20;

    toolTip.showNextButton = showNextButton;
    toolTip.arrowOffset = arrowOffset;

    const tObj = createToolTipTextObj(
      manager,
      toolTip,
      text[0],
      TIP_STICKY,
      (size.width - 60) / 100,
    );

    const closeButton = createAndAddLocalIconButton(manager, CLOSE);
    closeButton.moveMode = MoveMode.ANCHORED;
    closeButton.anchoredPos = anchoredPos(v2(1, 0), v2(-6, 6), v2(1, 0));
    setParent(closeButton, toolTip);

    toolTip.tObj = tObj;
    toolTip.flag = flag;
    toolTip.btnClose = closeButton;
    toolTip.text = text;
    toolTip.onFlagSet = onFlagSet;

    if (text.length > 1 || showNextButton) {
      toolTip.btnNext = createNavButton(
        toolTip,
        'Next',
        anchoredPos(v2(1, 1), v2(-10, -5), v2(1, 1)),
      );
      setVisibilityState(toolTip.btnNext, VisibilityState.DEFAULT);
      toolTip.height = tObj.height + BUTTON_PADDING;
    } else {
      toolTip.height = tObj.height;
    }

    toolTipMap.set(gameObject, toolTip);
  }
}

const TIMED_DURATION_MS = 5000;

export function showToolTip(
  gameObject: GameObject,
  label: string,
  style: 'simple' | 'tip' | 'timed' = 'simple',
  offset: V2 = { x: 0, y: 0 },
  arrowOffset = 0,
) {
  let toolTip: ToolTip = toolTipMap.get(gameObject);

  if (!toolTip) {
    const manager = gameObject.manager;
    const isSimpleOrTimed = style === 'simple' || style === 'timed';
    const baseOffset = isSimpleOrTimed ? v2(0, -2) : v2(0, -5);
    const shapeStyle = isSimpleOrTimed ? SHAPE_STYLES.NONE : SHAPE_STYLES.WHITE;
    const arrowSide = isSimpleOrTimed ? Side.NONE : Side.BELOW;

    toolTip = createToolTipBase(
      manager,
      gameObject,
      anchoredPos(v2(0.5, 0), addV2(baseOffset, offset), v2(0.5, 1)),
      shapeStyle,
      arrowSide,
    );

    const tObj = createToolTipTextObj(manager, toolTip, label, isSimpleOrTimed ? SIMPLE : TIP, 2);

    toolTip.width = tObj.width;
    toolTip.height = tObj.height;
    toolTip.tObj = tObj;
    toolTip.text = [label];
    toolTip.arrowOffset = arrowOffset;

    if (style === 'timed') {
      toolTip.shownAt = Date.now();
    }

    toolTipMap.set(gameObject, toolTip);
  }
}

export function hideToolTip(
  gameObject: GameObject,
  hideWhenSticky = false,
  setFlag = false,
  doContinue = false,
) {
  const toolTip = toolTipMap.get(gameObject);

  if (toolTip && (!toolTip.flag || hideWhenSticky)) {
    setVisibilityState(toolTip, VisibilityState.DISAPPEARING);

    toolTipMap.delete(gameObject);

    if (toolTip.onFlagSet) {
      toolTip.onFlagSet(doContinue);
    }
  }
}
